// src/components/Layout.js
import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import styled from 'styled-components';
import Navigation from './Navigation';
import AlertBanner from './AlertBanner';

// Main layout wrapper
const LayoutWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background};
`;

// Main content area - removed top padding as each page handles it
const Main = styled.main`
  flex: 1;
  position: relative;
`;

// Enhanced footer
const Footer = styled.footer`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  padding: ${({ theme }) => theme.spacing.xl} 0;
  margin-top: ${({ theme }) => theme.spacing['3xl']};
`;

// Text Link style - for navigation and external links
// Social icon link for footer
// Scroll to top button - Primary variant (circular)
const ScrollToTop = styled.button`
  position: fixed;
  bottom: ${({ theme }) => theme.spacing.xl};
  right: ${({ theme }) => theme.spacing.xl};
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.background};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  transition: all ${({ theme }) => theme.transitions.fast};
  opacity: ${({ $visible }) => $visible ? 1 : 0};
  pointer-events: ${({ $visible }) => $visible ? 'auto' : 'none'};
  z-index: 100;
  font-size: 1.25rem;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }

  &:focus {
    outline: 3px solid ${({ theme }) => theme.colors.primary}40;
    outline-offset: 2px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    bottom: ${({ theme }) => theme.spacing.lg};
    right: ${({ theme }) => theme.spacing.lg};
    width: 44px;
    height: 44px;
  }
`;

const Layout = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <LayoutWrapper>
      <AlertBanner />
      <Navigation />
      <Main>
        <Outlet />
      </Main>
      <Footer />

      <ScrollToTop
        onClick={scrollToTop}
        $visible={showScrollTop}
        aria-label="Scroll to top"
      >
        ↑
      </ScrollToTop>
    </LayoutWrapper>
  );
};

export default Layout;